import { create } from 'zustand'
import { shallow } from 'zustand/shallow'
import { convex, convexClient } from '../convexClient'
import { api } from '../lib/convexHelpers'
import { songsAPI } from '../services/api'
import toast from 'react-hot-toast'

/**
 * Processing store - handles song processing jobs
 * Manages job status, retries, filtering, and cleanup
 *
 * Processing jobs are synced from Convex reactive queries via useConvexSync.
 * Mutations go directly to Convex.
 */
const useProcessingStore = create((set, get) => ({
  // State
  processingJobs: new Map(),
  jobsFilter: 'all', // all, active, completed, failed
  jobsLoaded: false,
  jobsLoading: false,
  lastJobsCleanup: Date.now(),

  // Failed ingestions
  failedIngestions: [],
  failedIngestionsLoaded: false,
  failedIngestionsLoading: false,

  // Callbacks for cross-store communication
  onJobCompleted: null,

  // Actions

  /**
   * Load processing jobs from Convex
   */
  loadProcessingJobs: async (roomId = null, forceReload = false) => {
    const { jobsLoaded, jobsLoading } = get()

    if (jobsLoading || (jobsLoaded && !forceReload)) {
      return
    }

    set({ jobsLoading: true })

    try {
      const params = { limit: 100 }
      if (roomId) {
        params.roomId = roomId
      }

      const jobs = await convexClient.query(api.processingJobs.list, params)

      const jobsMap = new Map()
      ;(jobs || []).forEach(job => {
        jobsMap.set(job.jobId, job)
      })

      set({
        processingJobs: jobsMap,
        jobsLoaded: true,
        jobsLoading: false
      })

      get().cleanupOldJobs()
    } catch (error) {
      console.error('Failed to load processing jobs:', error)
      toast.error('Failed to load processing jobs')
      set({ jobsLoading: false })
    }
  },

  /**
   * Add a new processing job
   */
  addProcessingJob: (job) => {
    const { processingJobs } = get()
    const enhancedJob = { ...job, retryCount: job.retryCount ?? 0 }
    processingJobs.set(job.jobId, enhancedJob)
    set({ processingJobs: new Map(processingJobs) })
  },

  /**
   * Update a processing job (called by useConvexSync or legacy format)
   */
  updateProcessingJob: (payload) => {
    const { new: newJob, eventType } = payload
    const { processingJobs, onJobCompleted } = get()

    if (newJob) {
      const existingJob = processingJobs.get(newJob.jobId)
      const enhancedJob = {
        ...newJob,
        retryCount: existingJob?.retryCount ?? newJob.retryCount ?? 0
      }

      if (newJob.status === 'completed' || newJob.status === 'failed') {
        processingJobs.set(newJob.jobId, enhancedJob)
        set({ processingJobs: new Map(processingJobs) })

        setTimeout(() => {
          const { processingJobs: currentJobs } = get()
          if (currentJobs.has(newJob.jobId)) {
            currentJobs.delete(newJob.jobId)
            set({ processingJobs: new Map(currentJobs) })
          }
        }, 2000)
      } else {
        processingJobs.set(newJob.jobId, enhancedJob)
        set({ processingJobs: new Map(processingJobs) })
      }

      if (eventType === 'UPDATE') {
        if (newJob.status === 'completed') {
          toast.success('Song processed successfully!')
          if (onJobCompleted) {
            onJobCompleted()
          }
        } else if (newJob.status === 'failed') {
          toast.error('Song processing failed')
        }
      }
    }
  },

  /**
   * Remove a processing job (cancel) via Convex mutation
   */
  removeFromProcessing: async (jobId) => {
    const { processingJobs } = get()

    const jobToRemove = Array.from(processingJobs.values()).find(
      job => job._id === jobId || job.jobId === jobId
    )
    if (!jobToRemove) {
      console.warn('Processing job not found:', jobId)
      return
    }

    // Optimistic update
    const optimisticJobs = new Map(processingJobs)
    optimisticJobs.delete(jobToRemove.jobId)
    set({ processingJobs: optimisticJobs })

    try {
      await convex.mutation(api.processingJobs.deleteJob, { jobId: jobToRemove.jobId })
      toast.success('Processing job cancelled!')
    } catch (error) {
      console.error('Failed to cancel processing job:', error)

      // Restore on failure
      const { processingJobs: currentJobs } = get()
      const restoredJobs = new Map(currentJobs)
      restoredJobs.set(jobToRemove.jobId, jobToRemove)
      set({ processingJobs: restoredJobs })

      if (error.message?.includes('Can only cancel')) {
        toast.error('Cannot remove from queue, song is already processing')
      } else {
        toast.error('Failed to cancel processing job')
      }
    }
  },

  /**
   * Set jobs filter
   */
  setJobsFilter: (filter) => {
    set({ jobsFilter: filter })
  },

  /**
   * Get filtered jobs as array
   */
  getFilteredJobs: () => {
    const { processingJobs, jobsFilter } = get()
    const jobsArray = Array.from(processingJobs.values())

    switch (jobsFilter) {
      case 'active':
        return jobsArray.filter(job =>
          ['pending', 'processing'].includes(job.status)
        )
      case 'completed':
        return jobsArray.filter(job => job.status === 'completed')
      case 'failed':
        return jobsArray.filter(job => job.status === 'failed')
      default:
        return jobsArray
    }
  },

  /**
   * Get active jobs count
   */
  getActiveJobsCount: () => {
    const { processingJobs } = get()
    return Array.from(processingJobs.values()).filter(job =>
      ['pending', 'processing'].includes(job.status)
    ).length
  },

  /**
   * Get job statistics
   */
  getJobsStats: () => {
    const { processingJobs } = get()
    const jobs = Array.from(processingJobs.values())

    return jobs.reduce((stats, job) => {
      stats.total++
      stats[job.status] = (stats[job.status] || 0) + 1
      return stats
    }, {
      total: 0,
      pending: 0,
      processing: 0,
      completed: 0,
      failed: 0
    })
  },

  /**
   * Retry a failed processing job via backend API
   */
  retryProcessingJob: async (jobId) => {
    const { processingJobs } = get()
    const job = processingJobs.get(jobId)

    if (!job) {
      toast.error('Job not found')
      return
    }

    if (job.status !== 'failed') {
      toast.error('Only failed jobs can be retried')
      return
    }

    if ((job.retryCount || 0) >= 3) {
      toast.error('Maximum retries reached (3)')
      return
    }

    try {
      const updatedJob = { ...job, retryCount: (job.retryCount || 0) + 1 }
      processingJobs.set(jobId, updatedJob)
      set({ processingJobs: new Map(processingJobs) })

      await songsAPI.retryProcessingJob(jobId)
      toast.success('Job retry initiated')
    } catch (error) {
      console.error('Failed to retry job:', error)
      toast.error('Failed to retry job')

      processingJobs.set(jobId, job)
      set({ processingJobs: new Map(processingJobs) })
    }
  },

  /**
   * Cleanup old jobs to manage memory
   */
  cleanupOldJobs: () => {
    const { processingJobs, lastJobsCleanup } = get()

    if (Date.now() - lastJobsCleanup < 30 * 60 * 1000) {
      return
    }

    const cutoff = Date.now() - (6 * 60 * 60 * 1000)
    let cleanedCount = 0

    for (const [jobId, job] of processingJobs) {
      const jobTime = typeof job._creationTime === 'number' ? job._creationTime : new Date(job._creationTime).getTime()
      if (jobTime < cutoff && ['completed', 'failed'].includes(job.status)) {
        processingJobs.delete(jobId)
        cleanedCount++
      }
    }

    if (cleanedCount > 0) {
      set({
        processingJobs: new Map(processingJobs),
        lastJobsCleanup: Date.now()
      })
    }
  },

  // Failed ingestions management

  /**
   * Load failed ingestions from Convex
   */
  loadFailedIngestions: async (forceReload = false) => {
    const { failedIngestionsLoaded, failedIngestionsLoading } = get()

    if (failedIngestionsLoading || (failedIngestionsLoaded && !forceReload)) {
      return
    }

    set({ failedIngestionsLoading: true })

    try {
      const rawIngestions = await convexClient.query(api.failedIngestions.list, {})
      const failedIngestions = (rawIngestions || []).map((fi) => ({ ...fi, id: fi._id }))
      set({
        failedIngestions,
        failedIngestionsLoaded: true,
        failedIngestionsLoading: false
      })
    } catch (error) {
      console.error('Failed to load failed ingestions:', error)
      toast.error('Failed to load failed ingestions')
      set({ failedIngestionsLoading: false })
    }
  },

  /**
   * Acknowledge a failed ingestion via Convex mutation
   */
  acknowledgeFailedIngestion: async (failedIngestionId) => {
    try {
      await convex.mutation(api.failedIngestions.acknowledge, { id: failedIngestionId })

      const { failedIngestions } = get()
      const updated = failedIngestions.map(fi =>
        fi.id === failedIngestionId
          ? { ...fi, acknowledged: true }
          : fi
      )

      set({ failedIngestions: updated })
      toast.success('Failed ingestion acknowledged')
    } catch (error) {
      console.error('Failed to acknowledge failed ingestion:', error)
      toast.error('Failed to acknowledge failed ingestion')
    }
  },

  /**
   * Delete a failed ingestion via Convex mutation
   */
  deleteFailedIngestion: async (failedIngestionId) => {
    try {
      await convex.mutation(api.failedIngestions.remove, { id: failedIngestionId })

      const { failedIngestions } = get()
      const updated = failedIngestions.filter(fi => fi.id !== failedIngestionId)

      set({ failedIngestions: updated })
      toast.success('Failed ingestion deleted')
    } catch (error) {
      console.error('Failed to delete failed ingestion:', error)
      toast.error('Failed to delete failed ingestion')
    }
  },

  /**
   * Get failed ingestions statistics
   */
  getFailedIngestionsStats: () => {
    const { failedIngestions } = get()

    return failedIngestions.reduce((stats, fi) => {
      stats.total++
      if (fi.acknowledged) {
        stats.acknowledged++
      } else {
        stats.unacknowledged++
      }
      stats.byStage[fi.failureStage] = (stats.byStage[fi.failureStage] || 0) + 1
      return stats
    }, {
      total: 0,
      acknowledged: 0,
      unacknowledged: 0,
      byStage: {}
    })
  },

  /**
   * Set callback for job completion (cross-store communication)
   */
  setOnJobCompleted: (callback) => {
    set({ onJobCompleted: callback })
  },

  /**
   * Reset store state
   */
  reset: () => {
    set({
      processingJobs: new Map(),
      jobsFilter: 'all',
      jobsLoaded: false,
      jobsLoading: false,
      lastJobsCleanup: Date.now(),
      failedIngestions: [],
      failedIngestionsLoaded: false,
      failedIngestionsLoading: false
    })
  }
}))

// Selector hooks for granular subscriptions

export const useProcessingJobs = () => useProcessingStore(state => state.processingJobs)

export const useProcessingJobsState = () => useProcessingStore(
  state => ({
    processingJobs: state.processingJobs,
    jobsFilter: state.jobsFilter,
    jobsLoaded: state.jobsLoaded,
    jobsLoading: state.jobsLoading
  }),
  shallow
)

export const useFailedIngestions = () => useProcessingStore(
  state => ({
    failedIngestions: state.failedIngestions,
    failedIngestionsLoaded: state.failedIngestionsLoaded,
    failedIngestionsLoading: state.failedIngestionsLoading
  }),
  shallow
)

export default useProcessingStore
